/* ──────────────────────────────────────────────────────────────
 *  DialogShell — Reusable dialog wrapper
 *
 *  Provides: title bar (icon + text), scrollable content area,
 *  and an action-buttons footer.
 * ────────────────────────────────────────────────────────────── */

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

export interface DialogShellProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog requests to close (backdrop click, escape key). */
  onClose: () => void;
  /** Dialog title text. */
  title: React.ReactNode;
  /** Optional MUI icon element rendered before the title. */
  icon?: React.ReactNode;
  /** Color applied to the title text. */
  titleColor?: string;
  /** Maximum dialog width (px or MUI breakpoint). */
  maxWidth?: number | string;
  /** If true, backdrop/escape won't close the dialog. */
  persistent?: boolean;
  /** Content rendered inside the scrollable area. */
  children: React.ReactNode;
  /** Action buttons rendered in the footer. */
  actions?: React.ReactNode;
  /** Additional sx for the DialogContent area. */
  contentSx?: SxProps<Theme>;
}

const DialogShell: React.FC<DialogShellProps> = ({
  open,
  onClose,
  title,
  icon,
  titleColor,
  maxWidth = 500,
  persistent = false,
  children,
  actions,
  contentSx,
}) => (
  <Dialog
    open={open}
    onClose={persistent ? undefined : onClose}
    maxWidth={false}
    PaperProps={{
      elevation: 6,
      sx: { maxWidth, width: '100%' },
    }}
  >
    <DialogTitle
      sx={{
        fontWeight: 700,
        fontSize: '1.125rem',
        color: titleColor,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
      }}
    >
      {icon && (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      )}
      {title}
    </DialogTitle>

    <DialogContent sx={{ fontSize: '0.875rem', lineHeight: 1.6, ...contentSx }}>
      {children}
    </DialogContent>

    {actions && (
      <DialogActions sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        {actions}
      </DialogActions>
    )}
  </Dialog>
);

export default DialogShell;
